import type { Artifact, ArtifactType } from 'types/maxi-ai';
import { ARTIFACT_TYPES } from 'types/maxi-ai';

/**
 * Content segment - text, artifact, or loading artifact
 */
export interface ContentSegment {
  id: string;
  type: 'text' | 'artifact' | 'artifact-loading';
  content: string | Artifact;
  artifactType?: ArtifactType;
}

/**
 * Regex to match artifact tags with their IDs
 * Matches: <table sources="..." description="...">12345</table>, <line_chart>67890</line_chart>, etc.
 */
const ARTIFACT_TAG_REGEX =
  /<(table|line_chart|bar_chart)(?:\s+(?:[^>"']+|"[^"]*"|'[^']*')*)?>([a-zA-Z0-9]+)<\/\1>/g;

/**
 * Parse content into ordered segments of text and artifacts
 */
export const parseContentSegments = (
  text: string,
  artifacts: Record<string, Artifact>
): ContentSegment[] => {
  if (!text.trim()) return [];

  const segments: ContentSegment[] = [];
  let lastIndex = 0;

  // Reset regex state
  ARTIFACT_TAG_REGEX.lastIndex = 0;

  let match;
  while ((match = ARTIFACT_TAG_REGEX.exec(text)) !== null) {
    // Add text segment before this artifact (if any)
    if (match.index > lastIndex) {
      const textContent = text.slice(lastIndex, match.index);
      if (textContent.trim()) {
        segments.push({
          id: crypto.randomUUID(),
          type: 'text',
          content: textContent,
        });
      }
    }

    const artifactType = match[1] as ArtifactType;
    const artifactId = match[2];
    const artifact = artifacts[artifactId];

    if (artifact && artifact.status === 'complete' && artifact.content) {
      // Complete artifact with content
      segments.push({
        id: artifactId,
        type: 'artifact',
        content: artifact,
        artifactType,
      });
    } else if (artifact && artifact.status === 'failed') {
      // Failed artifact - show as text with error
      segments.push({
        id: crypto.randomUUID(),
        type: 'text',
        content: `[Artifact Error: ${artifact.error || 'Unknown error'}]`,
      });
    } else {
      // Loading artifact or artifact not found yet
      segments.push({
        id: artifactId,
        type: 'artifact-loading',
        content: '',
        artifactType,
      });
    }

    lastIndex = match.index + match[0].length;
  }

  // Add any remaining text
  const remainingText = text.slice(lastIndex);
  if (remainingText.trim()) {
    segments.push({
      id: crypto.randomUUID(),
      type: 'text',
      content: remainingText,
    });
  }

  // If no segments found, return the entire text as one segment
  return segments.length > 0
    ? segments
    : [
        {
          id: crypto.randomUUID(),
          type: 'text',
          content: text,
        },
      ];
};

/**
 * Get artifact type from string
 */
export const getArtifactType = (type: string): ArtifactType | null => {
  const validTypes = Object.values(ARTIFACT_TYPES);
  return validTypes.includes(type as ArtifactType)
    ? (type as ArtifactType)
    : null;
};

/**
 * Create a loading artifact placeholder
 */
export const createLoadingArtifact = (
  id: string,
  type: ArtifactType
): Artifact => {
  const baseArtifact = {
    id,
    type,
    status: 'processing' as const,
  };

  switch (type) {
    case ARTIFACT_TYPES.TABLE:
      return { ...baseArtifact, type: 'table' };
    case ARTIFACT_TYPES.LINE_CHART:
      return { ...baseArtifact, type: 'line_chart' };
    case ARTIFACT_TYPES.BAR_CHART:
      return { ...baseArtifact, type: 'bar_chart' };
    default:
      return { ...baseArtifact, type: 'table' }; // fallback
  }
};
